import { Router } from '@angular/router';
import { NavigationItem, ButtonData } from '../interfaces';
import { ButtonActionType, ButtonNavigationType } from '../enums';
import { isStoreRelatedPage, normalizeUrlForRouting } from './url-handlers';

const BUILT_IN_PAGE_ROUTES: Record<string, string> = {
  'order-status': '/orderstatus',
  'orderstatus': '/orderstatus',
  'terms-of-service': '/store-policy/terms',
  'privacy-policy': '/store-policy/privacy',
  'accessibility-policy': '/store-policy/accessibility',
  'payment-policy': '/store-policy/payment',
  'product-warranty': '/store-policy/product',
  'return-exchange-policy': '/store-policy/return-exchange',
  'shipping-policy': '/store-policy/shipping',
};

const MAILTO_BUILT_IN_PAGES = new Set(['contact-us']);
const DEFAULT_CONTACT_US_EMAIL = 'support@dealomega.com';

function resolveContactUsEmail(supportEmail?: string): string {
  const trimmed = supportEmail?.trim();
  return trimmed || DEFAULT_CONTACT_US_EMAIL;
}

export function getContactUsMailtoHref(supportEmail?: string): string {
  return `mailto:${resolveContactUsEmail(supportEmail)}`;
}

export function resolveButtonNavigationItem(button?: ButtonData | null): NavigationItem | null {
  if (!button?.enabled || button.actionType !== ButtonActionType.NavigateTo) {
    return null;
  }

  // New payloads provide the fully-formed navigation item directly.
  if (button.navigationItem) {
    return button.navigationItem;
  }

  // Backward compatibility for legacy button fields (`navigateType`, `targetPage`, etc.).
  if (!button.navigateType) {
    return null;
  }

  switch (button.navigateType) {
    case ButtonNavigationType.NavigateToProduct:
      return {
        label: button.text || '',
        targetType: 'site-page',
        pageSlug: '/dp',
        targetProductId: button.targetProduct?.id || null,
        targetProductSlug: button.targetProduct?.slug,
        pageSection: button.targetPageSectionId,
      };
    case ButtonNavigationType.NavigateToCollection:
      return {
        label: button.text || '',
        targetType: 'site-page',
        pageSlug: '/gp',
        targetCollectionId: button.targetCollection?.id || null,
        targetCollectionSlug: button.targetCollection?.slug,
        pageSection: button.targetPageSectionId,
      };
    case ButtonNavigationType.NavigateToWebsite:
      return {
        label: button.text || '',
        targetType: 'website',
        websiteUrl: button.targetPage,
      };
    case ButtonNavigationType.NavigateToPage:
      return {
        label: button.text || '',
        targetType: 'site-page',
        pageSlug: button.targetPage,
        pageSection: button.targetPageSectionId,
      };
    default:
      return null;
  }
}

export function navigateByNavigationItem(router: Router, item: NavigationItem, baseUrl = '', supportEmail?: string): void {
  const targetType = item?.targetType;
  const section = item?.pageSection || undefined;
  const queryParams = section ? { section } : undefined;

  if (targetType === 'website') {
    if (item.websiteUrl) {
      window.open(item.websiteUrl, '_blank', 'noopener,noreferrer');
    }
    return;
  }

  if (targetType === 'built-in-site-page') {
    const builtInPage = item.builtInPage || '';
    if (MAILTO_BUILT_IN_PAGES.has(builtInPage)) {
      window.location.href = getContactUsMailtoHref(supportEmail);
      return;
    }

    const route = BUILT_IN_PAGE_ROUTES[builtInPage];
    if (route) {
      router.navigate([route], { queryParams });
    }
    return;
  }

  if (targetType === 'site-page' || targetType === 'site-page-section' || targetType === 'custom') {
    const targetPage = item.pageSlug || item.customSlug;
    if (!targetPage) return;

    if (targetPage === '__current_page__') {
      // Section links on the same page should scroll instead of routing.
      if (section) {
        const element = document.getElementById(section);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }
      return;
    }

    if (isStoreRelatedPage(targetPage)) {
      // Store-policy and similar routes are intentionally hard reloaded.
      window.location.href = `/${targetPage.replace(/^\/+/, '')}`;
      return;
    }

    // Legacy page slugs `/dp` and `/gp` need the selected entity slug appended.
    if(targetPage === '/dp' && item.targetProductSlug) {
      router.navigate([`${baseUrl}/dp`, item.targetProductSlug], { queryParams });
      return;
    } else if(targetPage === '/gp' && item.targetCollectionSlug) {
      router.navigate([`${baseUrl}/gp`, item.targetCollectionSlug], { queryParams });
      return;
    }

    const pathSegments = normalizeUrlForRouting(baseUrl || '/', targetPage);
    router.navigate(pathSegments, { queryParams });
    return;
  }
}

export function getNavigationItemHref(item: NavigationItem, supportEmail?: string): string | null {
  if (!item) return null;

  if (item.targetType === 'website') {
    return item.websiteUrl || null;
  }

  if (item.targetType === 'built-in-site-page') {
    const target = item.builtInPage || '';
    if (MAILTO_BUILT_IN_PAGES.has(target)) {
      return getContactUsMailtoHref(supportEmail);
    }
    return BUILT_IN_PAGE_ROUTES[target] || null;
  }

  if (item.targetType === 'site-page' || item.targetType === 'site-page-section' || item.targetType === 'custom') {
    const slug = item.pageSlug || item.customSlug;
    if(slug === '/dp' && item.targetProductSlug) {
      return `/dp/${item.targetProductSlug}`;
    } else if(slug === '/gp' && item.targetCollectionSlug) {
      return `/gp/${item.targetCollectionSlug}`;
    }
    return slug ? `/${slug.replace(/^\/+/, '')}` : null;
  }

  return null;
}
